import React, { useState, useEffect } from "react";
import MbLeftBar from "./components/MbLeftBar";
import MbChat from "./components/MbChat";
import MbSubscriptionModal from "./components/MbSubscriptionModal";
import { AiOutlineClose } from "react-icons/ai";
import "./mbbot.css";

import themeClasses from "./themeClasses";

export default function App() {
  const [chats, setChats] = useState(() => {
    const saved = localStorage.getItem("mbbot-chats");
    return saved ? JSON.parse(saved) : [];
  });

  const [activeChatId, setActiveChatId] = useState(() => {
    const saved = localStorage.getItem("mbbot-activeChatId");
    return saved ? JSON.parse(saved) : null;
  });

  const [currentTheme, setCurrentTheme] = useState(
    () => localStorage.getItem("mbbot-theme") || "light"
  );

  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

  const [projects, setProjects] = useState(() => {
    const saved = localStorage.getItem("mb_project_chat_data");
    return saved ? JSON.parse(saved) : [];
  });

  const [activeProjectChatId, setActiveProjectChatId] = useState(null);

  // === Effects ===
  useEffect(() => {
    document.body.setAttribute("data-theme", currentTheme);
    localStorage.setItem("mbbot-theme", currentTheme);
  }, [currentTheme]);

  useEffect(() => {
    localStorage.setItem("mbbot-chats", JSON.stringify(chats));
  }, [chats]);

  useEffect(() => {
    if (activeChatId !== null) {
      localStorage.setItem("mbbot-activeChatId", JSON.stringify(activeChatId));
    }
  }, [activeChatId]);

  useEffect(() => {
    localStorage.setItem("mb_project_chat_data", JSON.stringify(projects));
  }, [projects]);

  // First-time setup
  useEffect(() => {
    if (chats.length === 0) handleNewChat();
    else if (!activeChatId && chats.length > 0) setActiveChatId(chats[0].id);
  }, []);

  // === Chat Handlers ===
  const handleUpdateChatMessages = (updatedMessages, chatId) => {
    setChats((prev) =>
      prev.map((chat) => {
        const firstUserMessage = updatedMessages.find(
          (m) => m.sender === "user"
        );
        const autoName =
          firstUserMessage?.text?.trim().slice(0, 40) || chat.name;
        return chat.id === chatId
          ? {
              ...chat,
              messages: updatedMessages,
              name: chat.name === "New Chat" ? autoName : chat.name,
            }
          : chat;
      })
    );
  };

  const handleSelectChat = (chatId, isProjectChat = false) => {
    if (isProjectChat) {
      setActiveProjectChatId(chatId);
      setActiveChatId(null);
    } else {
      setActiveChatId(chatId);
      setActiveProjectChatId(null);
    }
  };

  const handleNewChat = () => {
    const newId = Date.now();
    const newChat = { id: newId, name: "New Chat", messages: [] };
    setChats((prev) => [...prev, newChat]);
    setActiveChatId(newId);
    setActiveProjectChatId(null);
    localStorage.setItem(`mbChatHistory_${newId}`, JSON.stringify([]));
  };

  const handleDeleteChat = (chatId) => {
    setChats((prev) => {
      const remaining = prev.filter((c) => c.id !== chatId);
      if (remaining.length === 0) {
        const newId = Date.now();
        const newChat = { id: newId, name: "New Chat", messages: [] };
        localStorage.setItem(`mbChatHistory_${newId}`, JSON.stringify([]));
        setActiveChatId(newId); // Set active chat immediately
        return [newChat];
      }
      if (activeChatId === chatId) {
        setActiveChatId(remaining[0].id);
      }
      return remaining;
    });
    localStorage.removeItem(`mbChatHistory_${chatId}`);
  };

  const handleEditChat = (chatId, newName) => {
    setChats((prev) =>
      prev.map((c) => (c.id === chatId ? { ...c, name: newName } : c))
    );
  };

  // === Project Handlers ===
  const handleUpdateProjectChatMessages = (updatedMessages, projectChatId) => {
    setProjects((prev) =>
      prev.map((proj) =>
        proj.chat.id === projectChatId
          ? {
              ...proj,
              chat: {
                ...proj.chat,
                messages: updatedMessages,
                name:
                  updatedMessages
                    .find((m) => m.sender === "user")
                    ?.text?.trim()
                    .slice(0, 40) || proj.chat.name,
              },
            }
          : {
              ...proj,
              subprojects: proj.subprojects?.map((sub) =>
                sub.chat.id === projectChatId
                  ? {
                      ...sub,
                      chat: {
                        ...sub.chat,
                        messages: updatedMessages,
                        name:
                          updatedMessages
                            .find((m) => m.sender === "user")
                            ?.text?.trim()
                            .slice(0, 40) || sub.chat.name,
                      },
                    }
                  : sub
              ),
            }
      )
    );
  };

  const handleAddProject = (projectName) => {
    if (!projectName) return;

    const timestamp = Date.now();
    const newChatId = `chat-${timestamp}`;
    const newProj = {
      id: `proj-${timestamp}`,
      name: projectName,
      chat: { id: newChatId, name: "New Chat", messages: [] },
      subprojects: [],
    };

    setProjects((prev) => [...prev, newProj]);
    setActiveProjectChatId(newChatId);
    setActiveChatId(null);
    localStorage.setItem(`mbChatHistory_${newChatId}`, JSON.stringify([]));
  };

  const handleAddSubproject = (projectId, subName) => {
    if (!subName) return;

    const timestamp = Date.now();
    const newSubChatId = `chat-${timestamp}`;
    const newSub = {
      id: `sub-${timestamp}`,
      name: subName,
      chat: { id: newSubChatId, name: "New Chat", messages: [] },
    };

    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? { ...p, subprojects: [...(p.subprojects || []), newSub] }
          : p
      )
    );

    setActiveProjectChatId(newSubChatId);
    setActiveChatId(null);
    localStorage.setItem(`mbChatHistory_${newSubChatId}`, JSON.stringify([]));
  };

  const handleDeleteProject = (projectId) => {
    setProjects((prev) => prev.filter((p) => p.id !== projectId));
    if (activeProjectChatId) {
      const exists = projects.find((p) => p.chat.id === activeProjectChatId);
      if (exists?.id === projectId) setActiveProjectChatId(null);
    }
  };

  const handleDeleteSubproject = (projectId, subId) => {
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? {
              ...p,
              subprojects: p.subprojects.filter((s) => s.id !== subId),
            }
          : p
      )
    );

    const subChatKey = projects
      .find((p) => p.id === projectId)
      ?.subprojects.find((s) => s.id === subId)?.chat.id;

    if (subChatKey) {
      localStorage.removeItem(`mbChatHistory_${subChatKey}`);
      if (activeProjectChatId === subChatKey) setActiveProjectChatId(null);
    }
  };

  const handleEditProject = (projectId, newName) => {
    if (!newName) return;
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? {
              ...p,
              name: newName,
              chat: { ...p.chat, name: `${newName} Chat` },
            }
          : p
      )
    );
  };

  const handleEditSubproject = (projectId, subId, newName) => {
    if (!newName) return;
    setProjects((prev) =>
      prev.map((p) =>
        p.id === projectId
          ? {
              ...p,
              subprojects: p.subprojects.map((s) =>
                s.id === subId
                  ? {
                      ...s,
                      name: newName,
                      chat: { ...s.chat, name: `${newName} Chat` },
                    }
                  : s
              ),
            }
          : p
      )
    );
  };

  const handleToggleSubscription = () =>
    setShowSubscriptionModal(!showSubscriptionModal);
  const handleChangeTheme = (theme) => setCurrentTheme(theme);
  const handleSidebarToggle = (collapsed) => setSidebarCollapsed(collapsed);

  const activeChat = chats.find((c) => c.id === activeChatId);
  const activeProjectChatObj = (() => {
    for (const proj of projects) {
      if (proj.chat.id === activeProjectChatId) return proj.chat;
      const sub = proj.subprojects?.find(
        (s) => s.chat.id === activeProjectChatId
      );
      if (sub) return sub.chat;
    }
    return null;
  })();

  return (
    <div className={`${themeClasses[currentTheme].body} App min-h-screen`}>
      <div className="mb-main-wrapper w-full max-w-full h-screen flex">
        <MbLeftBar
          chats={chats}
          activeChatId={activeChatId}
          onSelectChat={handleSelectChat}
          onNewChat={handleNewChat}
          onDeleteChat={handleDeleteChat}
          onEditChat={handleEditChat}
          onToggleSubscription={handleToggleSubscription}
          onChangeTheme={handleChangeTheme}
          currentTheme={currentTheme}
          onSidebarToggle={handleSidebarToggle}
          projects={projects}
          onAddProject={handleAddProject}
          onDeleteProject={handleDeleteProject}
          onEditProject={handleEditProject}
          onAddSubproject={handleAddSubproject}
          onDeleteSubproject={handleDeleteSubproject}
          onEditSubproject={handleEditSubproject}
        />

        <div
          className={`${
            themeClasses[currentTheme].chatbotWrapper
          } mb-chatbot-wrapper p-5 pb-20 pr-1 absolute top-0 right-0 bottom-0 transition-left-[0.3s] duration-300 ${
            sidebarCollapsed ? "left-[60px]" : "left-[250px]"
          }`}
        >
          {activeChatId && (
            <MbChat
              key={activeChatId}
              chatId={activeChatId}
              initialContent={activeChat?.messages || []}
              onUpdateContent={(msgs) =>
                handleUpdateChatMessages(msgs, activeChatId)
              }
              onNewChat={handleNewChat}
              currentTheme={currentTheme}
            />
          )}

          {activeProjectChatId && activeProjectChatObj && (
            <MbChat
              key={activeProjectChatId}
              chatId={activeProjectChatId}
              initialContent={activeProjectChatObj.messages || []}
              onUpdateContent={(msgs) =>
                handleUpdateProjectChatMessages(msgs, activeProjectChatId)
              }
              currentTheme={currentTheme}
            />
          )}
        </div>
      </div>

      {showSubscriptionModal && (
        <div
          className="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={handleToggleSubscription}
        >
          <div
            className={`${themeClasses[currentTheme].modal} ${themeClasses[currentTheme].pricingModal} pricing-modal absolute flex items-center inset-[10px] p-5 rounded-xl shadow-lg overflow-y-auto`}
            onClick={(e) => e.stopPropagation()}
          >
            <MbSubscriptionModal currentTheme={currentTheme} />
            <button
              className="modalClose absolute top-5 right-5 bg-none border-0"
              onClick={handleToggleSubscription}
            >
              <AiOutlineClose
                className={` text-xl ${
                  currentTheme === "dark" ? "text-white" : "text-black"
                }`}
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
